import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { PosthogSDKFactory, type IPosthogSdk } from "@repo/posthog-sdk";
import type { FlagName } from "src/posthog/flags";
import { useEnv } from "./env.context";
import { LoadingScreen } from "src/components/LoadingScreen";

const posthogContext = createContext<IPosthogSdk | null>(null);

export function usePosthog(): IPosthogSdk {
  const ctx = useContext(posthogContext);
  if (!ctx) throw new Error("usePosthog must be used within PosthogProvider");
  return ctx;
}

// Subscribes to a PostHog feature flag and returns its current boolean value.
// Re-renders whenever the SDK notifies of flag changes (local-mode toggles or
// prod-mode PostHog updates). Defaults to false before the first evaluation.
export function useFeatureFlag(flagName: FlagName): boolean {
  const posthog = usePosthog();
  const [enabled, setEnabled] = useState<boolean>(false);

  useEffect(() => {
    // onFeatureFlags fires the handler once on subscribe, so we get the
    // initial value and every subsequent change via the same callback.
    return posthog.onFeatureFlags(() => {
      setEnabled(posthog.isFlagEnabled(flagName) ?? false);
    });
  }, [posthog, flagName]);

  return enabled;
}

export function useFlagIdentity() {
  const posthog = usePosthog();
  return {
    identify: posthog.identify.bind(posthog),
    capture: posthog.capture.bind(posthog),
    logout: posthog.logout.bind(posthog),
  };
}

export function PosthogProvider({ children }: { children: ReactNode }) {
  const env = useEnv();
  const [sdk, setSdk] = useState<IPosthogSdk | null>(null);

  useEffect(() => {
    let cancelled = false;
    const instance = PosthogSDKFactory.createInstance({
      mode: env.posthogMode,
      apiKey: env.posthogApiKey || undefined,
      apiHost: env.posthogHost || undefined,
    });
    instance
      .init()
      .then(() => {
        if (!cancelled) setSdk(instance);
      })
      .catch((err) => {
        // Even on init failure, expose the instance so flag reads return their
        // default (undefined → false) instead of blocking the whole tree.
        console.error("PosthogSDK init failed:", err);
        if (!cancelled) setSdk(instance);
      });
    return () => {
      cancelled = true;
    };
  }, [env.posthogMode, env.posthogApiKey, env.posthogHost]);

  if (!sdk) return <LoadingScreen />;

  return <posthogContext.Provider value={sdk}>{children}</posthogContext.Provider>;
}
